import { Save } from '@mui/icons-material'
import { Box, Button, CircularProgress, Skeleton, Stack, Typography, alpha } from '@mui/material'

import React from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  save: () => void
  disableSubmission: boolean
  loading: boolean
  // When provided, the total is shown on the RHS (V2 reorder layout, where the
  // footer is rendered bare). Omitted in the legacy ConsolidatedBasket layout,
  // which already renders the total alongside the footer itself.
  grandTotal?: string
  taxAcronym?: string
}

// Mirrors ReorderBasketFooter's layout (pill button on the LHS, total on the
// RHS, 12/12/14 edge padding) so the standing-order Save footer is visually
// consistent with the regular checkout basket and isn't left flush/unpadded.
const StandingOrderFoooter: React.FC<Props> = ({
  save,
  disableSubmission,
  loading,
  grandTotal,
  taxAcronym,
}) => {
  const { t } = useTranslation()

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent={grandTotal ? 'space-between' : 'flex-start'}
      spacing={1.25}
      sx={{ padding: '12px 12px 14px' }}>
      <Button
        color="primary"
        variant="contained"
        onClick={save}
        disabled={disableSubmission || loading}
        startIcon={
          loading ? <CircularProgress size={14} color="inherit" /> : <Save sx={{ fontSize: 15 }} />
        }
        sx={{
          borderRadius: '100px',
          padding: '10px 20px',
          fontSize: 13,
          fontWeight: 700,
          textTransform: 'none',
          letterSpacing: '.1px',
          boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`,
          '&:hover': {
            boxShadow: (theme) => `0 4px 14px ${alpha(theme.palette.primary.main, 0.45)}`,
            transform: 'translateY(-1px)',
          },
        }}>
        {t('marketplace:save')}
      </Button>
      {grandTotal ? (
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
            {t('marketplace:total')}{' '}
            <Box component="span" sx={{ fontSize: 10 }}>
              ({t('marketplace:inc', { taxAcronym })})
            </Box>
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={80} sx={{ ml: 'auto' }} />
          ) : (
            <Typography
              sx={{
                fontSize: 19,
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: '-0.3px',
              }}>
              {grandTotal}
            </Typography>
          )}
        </Box>
      ) : null}
    </Stack>
  )
}

export default StandingOrderFoooter
